import { type paths } from "@platform/api-spec";
import { ApiError } from "@platform/shared/errors";
import { QueryClient, infiniteQueryOptions, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import * as React from "react";
import { useSWRConfig } from "swr";
import api from "~/shared/api/core/api";
import { BrainstormResponse, JobResponse } from "~/shared/api/jobs/jobs.types";
import { jobsListDataTransform } from "~/shared/api/jobs/jobs.utils";
import { Params } from "~/shared/api/types/params";
import { client } from "~/shared/lib/client";
import { INote } from "~/shared/types/note.types";
import { PaginatedResponse } from "~/shared/types/pagination.types";
import { ensureApiData } from "~/shared/util/api.helpers";
import { createInfinitePaginationConfig } from "~/shared/util/createInfinitePaginationConfig";
import { createSWRKeyChecker } from "~/shared/util/createSwrKeyChecker";

export const JOBS_KEYS = {
  all: "jobs" as const,
  list: (params: Params) => {
    return [JOBS_KEYS.all, "list", params] as const;
  },
  detail: (id: string | number) => [JOBS_KEYS.all, String(id)] as const,
  brainstorms: (id: string | number) => [...JOBS_KEYS.detail(id), "brainstorms"] as const,
  brainstormsList: (id: string | number, params: Params) => [...JOBS_KEYS.brainstorms(id), params] as const,
  process: (id: string | number) => [...JOBS_KEYS.detail(id), "process"] as const,
  processList: (id: string | number, params: Params) => [...JOBS_KEYS.process(id), params] as const,
  statuses: (id: string | number) => [...JOBS_KEYS.detail(id), "statuses"] as const,
  statusesList: (id: string | number, params: Params) => [...JOBS_KEYS.statuses(id), params] as const,
  statusesLog: (id: string | number) => [...JOBS_KEYS.detail(id), "statusesLog"] as const,
  statusesLogList: (id: string | number, params: Params) => [...JOBS_KEYS.statusesLog(id), params] as const,
  potentialParticipants: (id: string | number) => [...JOBS_KEYS.detail(id), "potentialParticipants"] as const,
  potentialParticipantsList: (id: string | number, params: Params) =>
    [...JOBS_KEYS.potentialParticipants(id), params] as const,
  checkIsJobSaved: (id: string) => [...JOBS_KEYS.detail(id), "isSaved"] as const,
  roleGroupsList: (id: string | number, params: Params) => [...JOBS_KEYS.detail(id), "role-group", params] as const,
  jobQuestions: (id: string | number) => [...JOBS_KEYS.detail(id), "job-questions"] as const,
  activeJobQuestionsList: (id: string | number, type?: "job" | "company" | "global", params?: Params) =>
    [...JOBS_KEYS.jobQuestions(id), "active", type ?? "all", params] as const,
  archivedJobQuestionsList: (id: string | number) => [...JOBS_KEYS.jobQuestions(id), "archived"] as const,
  closingNotes: (jobId: string) => [...JOBS_KEYS.detail(jobId), "closingNotes"] as const,
  interviewPlan: (jobId: string) => {
    return [...JOBS_KEYS.detail(jobId), "interview-plan"] as const;
  },
  jobInterviewRoundTypes: (jobId: string) => {
    return [...JOBS_KEYS.detail(jobId), "interview-round-types"] as const;
  },
  member: {
    all: () => [JOBS_KEYS.all, "member"] as const,
    detail: (jobId: string | number) => [...JOBS_KEYS.member.all(), String(jobId)] as const,
    gameplanDetail: (jobId: string | number) => [...JOBS_KEYS.member.detail(jobId), "gameplan"] as const,
  } as const,
  preview: (jobId: string | number) => [JOBS_KEYS.all, "preview", String(jobId)] as const,
  gamePlanDetail: (gamePlanId: string | number) => [JOBS_KEYS.all, "gameplan", String(gamePlanId)] as const,
};

export const JOBS_QUERIES = {
  list: (params: Params) => {
    return queryOptions({
      queryKey: JOBS_KEYS.list(params),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<JobResponse>>("/jobs", {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  detail: (id: string) => {
    return queryOptions({
      queryKey: JOBS_KEYS.detail(id),
      queryFn: async ({ signal }) => {
        const response = await api.get<JobResponse>(`/jobs/${id}`, {
          signal,
        });

        return response.data;
      },
    });
  },
  brainstormsList: (id: string, params = {}) => {
    return queryOptions({
      queryKey: JOBS_KEYS.brainstormsList(id, params),
      queryFn: async ({ signal }) => {
        const response = await api.get<BrainstormResponse>(`/jobs/${id}/brainstorm`, {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  processList: (id: string, params = {}) => {
    return queryOptions({
      queryKey: JOBS_KEYS.processList(id, params),
      queryFn: async ({ signal }) => {
        const response = await api.get(`/jobs/${id}/process`, {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  statusesList: (id: string, params = {}) => {
    return queryOptions({
      queryKey: JOBS_KEYS.statusesList(id, params),
      queryFn: async ({ signal }) => {
        const response = await api.get(`/jobs/${id}/statuses`, {
          signal,
          ...params,
        });

        return response.data;
      },
    });
  },
  statusesLogList: (id: string, params = {}) => {
    return queryOptions({
      queryKey: JOBS_KEYS.statusesLogList(id, params),
      queryFn: async ({ signal }) => {
        const response = await api.get(`/jobs/${id}/statuses/log`, {
          signal,
          ...params,
        });

        return response.data;
      },
    });
  },
  potentialParticipantsList: (id: string, params = {}) => {
    return queryOptions({
      queryKey: JOBS_KEYS.potentialParticipantsList(id, params),
      queryFn: async ({ signal }) => {
        const response = await api.get(`/jobs/${id}/potential-participants`, {
          signal,
          ...params,
        });

        return response.data;
      },
    });
  },
  closingNotesList: (jobId: string) => {
    return queryOptions({
      queryKey: JOBS_KEYS.closingNotes(jobId),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<INote>>(`/jobs/${jobId}/closing-notes`, {
          params: {
            skip: 0,
            limit: 100,
          },
          signal,
        });

        return response.data;
      },
    });
  },
  infiniteJobsList: (params: { [key: string]: unknown }) => {
    const paginationConfig = createInfinitePaginationConfig(
      {
        orderBy: "highPotential",
        orderDir: "DESC",
        ...params,
      },
      20,
    );

    return infiniteQueryOptions({
      queryFn: async ({ pageParam = 1, signal }) => {
        const response = await api.get<
          PaginatedResponse<{
            id: number;
            title: string;
            aggregates?: { highPriority?: number };
          }>
        >("/jobs", {
          params: paginationConfig.queryFnParams(pageParam),
          signal,
        });

        return response.data;
      },
      queryKey: JOBS_KEYS.list({
        params: paginationConfig.keyParams,
      }),
      select: jobsListDataTransform,
      ...paginationConfig.opts,
    });
  },
  checkIsJobSaved: (id: string) => {
    return queryOptions({
      queryKey: JOBS_KEYS.checkIsJobSaved(id),
      queryFn: async () => {
        const response = await api.post<boolean>(`/jobs/${id}/actions/checkIsSaved`, {
          jobId: id,
        });

        return response.data;
      },
    });
  },
  infiniteRoleGroupsList: (id: string, params: { [key: string]: unknown }) => {
    const paginationConfig = createInfinitePaginationConfig(params, 50);

    return infiniteQueryOptions({
      queryKey: JOBS_KEYS.roleGroupsList(id, {
        params: paginationConfig.keyParams,
      }),
      queryFn: async ({ pageParam = 1, signal }) => {
        const { data, error } = await client.GET("/jobs/{jobId}/role-groups", {
          params: {
            path: {
              jobId: parseInt(id),
            },
            query: paginationConfig.queryFnParams(pageParam),
          },
          signal,
        });

        if (error) {
          throw new Error("Error fetching role groups");
        }

        return data;
      },
      ...paginationConfig.opts,
    });
  },
  activeJobQuestionsList: (
    jobId: string,
    type?: "job" | "company" | "global",
    params: paths["/jobs/{jobId}/assigned-questions"]["get"]["parameters"]["query"] = {},
  ) => {
    return queryOptions({
      queryKey: JOBS_KEYS.activeJobQuestionsList(jobId, type, { params }),
      queryFn: async ({ signal }) => {
        const { data } = await client.GET("/jobs/{jobId}/assigned-questions", {
          params: {
            path: {
              jobId: parseInt(jobId),
            },
            query: {
              limit: 50,
              skip: 0,
              status: "active",
              type,
              ...params,
            },
          },
          signal,
        });

        return ensureApiData(data, "GET /jobs/{jobId}/assigned-questions");
      },
    });
  },
  archivedJobQuestionsList: (jobId: string) => {
    return queryOptions({
      queryKey: JOBS_KEYS.archivedJobQuestionsList(jobId),
      queryFn: async ({ signal }) => {
        const { data } = await client.GET("/jobs/{jobId}/assigned-questions", {
          params: {
            path: {
              jobId: parseInt(jobId),
            },
            query: {
              limit: 50,
              skip: 0,
              status: "archived",
            },
          },
          signal,
        });

        return ensureApiData(data, "GET /jobs/{jobId}/assigned-questions");
      },
    });
  },
  interviewPlan: (jobId: string) => {
    return queryOptions({
      queryKey: JOBS_KEYS.interviewPlan(jobId),
      queryFn: async ({ signal }) => {
        try {
          const { data } = await client.GET("/jobs/{jobId}/interview-plan", {
            params: { path: { jobId: parseInt(jobId) } },
            signal,
          });
          return ensureApiData(data, "GET /jobs/{jobId}/interview-plan");
        } catch (error) {
          // Allow 404s to return null for missing interview plans
          if (error instanceof ApiError && error.details.status === 404) {
            return null;
          }
          throw error;
        }
      },
    });
  },
  jobInterviewRoundTypes: (jobId: string) => {
    return queryOptions({
      queryKey: JOBS_KEYS.jobInterviewRoundTypes(jobId),
      queryFn: async ({ signal }) => {
        const { data } = await client.GET("/jobs/{jobId}/interview-round-types", {
          params: {
            path: {
              jobId: parseInt(jobId),
            },
          },
          signal,
        });

        return data;
      },
    });
  },
  gamePlanDetail: (gamePlanId: string) => {
    return queryOptions({
      queryKey: JOBS_KEYS.gamePlanDetail(gamePlanId),
      queryFn: async ({ signal }) => {
        const response = await api.get(`jobs/gameplan/${gamePlanId}`, {
          signal,
        });

        return response.data;
      },
    });
  },
  member: {
    detail: (jobId: number) => {
      return queryOptions({
        queryKey: JOBS_KEYS.member.detail(jobId),
        queryFn: async ({ signal }) => {
          const { data } = await client.GET("/member/jobs/{jobId}", {
            params: {
              path: {
                jobId,
              },
            },
            signal,
          });

          return ensureApiData(data, "GET /member/jobs/{jobId}");
        },
      });
    },
    gameplanDetail: (gamePlanId: string) => {
      return queryOptions({
        queryKey: JOBS_KEYS.member.gameplanDetail(gamePlanId),
        queryFn: async ({ signal }) => {
          const response = await api.get(`jobs/gameplan/${gamePlanId}`, {
            signal,
          });

          return response.data;
        },
      });
    },
  },
};

export async function getJobs(params: Params, signal?: AbortSignal) {
  const response = await api.get(`/jobs`, {
    ...params,
    signal,
  });

  return response.data;
}

export async function prefetchJobDetail(id: string, queryClient: QueryClient) {
  return queryClient.ensureQueryData(JOBS_QUERIES.detail(id));
}

export function useSuspenseJobDetail(id: string) {
  return useSuspenseQuery(JOBS_QUERIES.detail(id));
}

/**
 * Custom hook to invalidate all participant-related cache entries in SWR
 *
 * @returns Function to trigger cache invalidation for participant data
 */
export function useInvalidateJobsCache() {
  const { mutate } = useSWRConfig();

  return React.useCallback(() => {
    mutate(createSWRKeyChecker("jobs"));
  }, [mutate]);
}
